import apiClient from './apiClient';

/** @param {unknown} animalData @param {number | null} [tenantId] platform system admin context */
export const createAnimal = async (animalData, tenantId = null) => {
    try {
        const config = {};
        if (tenantId != null) config.params = { tenant_id: tenantId };
        const response = await apiClient.post('/animal/create_animal', animalData, config);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export async function updateAnimal(animalId, payload, tenantId = null) {
    try {
        const config = {};
        if (tenantId != null) config.params = { tenant_id: tenantId };
        const res = await apiClient.put(`/animal/update_animal/${animalId}`, payload, config);
        return res.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
}


export const getAnimals = async (page = 1, perPage = 10, tenantId = null) => {
    try {
        const params = { page, per_page: perPage };
        if (tenantId != null) params.tenant_id = tenantId;
        const response = await apiClient.get('/animal/get_animals', { params });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};


export const getAnimalById = async (id, tenantId = null) => {
    try {
        const config = {};
        if (tenantId != null) config.params = { tenant_id: tenantId };
        const response = await apiClient.get(`/animal/get_animal/${id}`, config);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Search dams (female animals) by query string.
 * Backend: GET /animal/search_dams?q=...
 * Returns an array of AnimalResponse (at least { id, tag_number, alias, gender, ... }).
 */
export const searchDams = async (q, tenantId = null) => {
    if (!q || !q.trim()) return [];
    try {
        const params = { q };
        if (tenantId != null) params.tenant_id = tenantId;
        const response = await apiClient.get('/animal/search_dams', { params });
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        // Surface empty on error; UI will show no results
        return [];
    }
};

/**
 * Search sires (male animals) by query string.
 * Backend: GET /animal/search_sires?q=...
 * Returns an array of AnimalResponse (at least { id, tag_number, alias, gender, ... }).
 */
export const searchSires = async (q, tenantId = null) => {
    if (!q || !q.trim()) return [];
    try {
        const params = { q };
        if (tenantId != null) params.tenant_id = tenantId;
        const response = await apiClient.get('/animal/search_sires', { params });
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        return [];
    }
};

/**
 * Type-ahead search for animals (tag, alias, etc. via backend `search`).
 * @param {string} q
 * @param {number|null} tenantId
 * @returns {Promise<Array>} animal rows (at least { id, tag_number, alias, ... })
 */
export const searchAnimalsQuick = async (q, tenantId = null) => {
    if (!q || !String(q).trim()) return [];
    try {
        const params = {
            page: 1,
            per_page: 25,
            search: String(q).trim(),
            sort_by: 'id',
            sort_dir: 'desc',
        };
        if (tenantId != null) params.tenant_id = tenantId;
        const response = await apiClient.get('/animal/get_animals', { params });
        const data = response.data;
        return Array.isArray(data?.items) ? data.items : [];
    } catch {
        return [];
    }
};

/** Kraal, breed, and tag values already used on animals for this org (form pickers). */
export const getAnimalFormOptions = async (tenantId = null) => {
    try {
        const config = {};
        if (tenantId != null) config.params = { tenant_id: tenantId };
        const response = await apiClient.get('/animal/form_options', config);
        const d = response.data || {};
        return {
            kraal_names: Array.isArray(d.kraal_names) ? d.kraal_names : [],
            breed_suggestions: Array.isArray(d.breed_suggestions) ? d.breed_suggestions : [],
            tag_suggestions: Array.isArray(d.tag_suggestions) ? d.tag_suggestions : [],
        };
    } catch {
        return { kraal_names: [], breed_suggestions: [], tag_suggestions: [] };
    }
};


/** NEW HELPERS for the Dashboard’s expected API */
async function listAnimals({ page = 1, pageSize = 100, tenantId = null } = {}) {
    const data = await getAnimals(page, pageSize, tenantId);
    const items = data?.items || data?.data || (Array.isArray(data) ? data : []) || [];
    const total =
        typeof data?.total === 'number' ? data.total : items.length;
    return { items, total };
}

async function getAnimalSummary(tenantId = null) {
    try {
        const config = {};
        if (tenantId != null) config.params = { tenant_id: tenantId };
        const res = await apiClient.get('/animal/summary', config);
        // Expected shape: { total: number, categories: { [category]: number } }
        const payload = res.data;
        if (typeof payload?.total === 'number') {
            // Normalize to the dashboard’s internal structure
            return { total: payload.total, byCategory: payload.categories || {} };
        }
    } catch (_) {
        // swallow and let dashboard fall back to client-side rollup
    }
    return null; // triggers fallback logic in the dashboard
}

export const animalApi = {
    listAnimals,
    getAnimalSummary,
};
// Add more animal-related API calls as needed